import { createReducer, on } from '@ngrx/store';

import { AlternativeOffersState, AlternativeOffersStateStatus, OrderSummaryStatus, Stage } from '../../interfaces';
import { AlternativeOffersActions } from '../actions';

export const initialState: AlternativeOffersState = Object.seal({
  status: AlternativeOffersStateStatus.INITIAL,
  alternativeOffers: undefined,
  selectedOffers: undefined,
  boundId: undefined,
  selectedDates: undefined,
  currentStage: Stage.selection,
  disruptedBoundIndex: -1,
  deeplinkSource: undefined,
  confirmationResponse: undefined,
  analyticsSent: false,
  sessionExpired: false,
  sessionStartedMs: undefined,
  booking: undefined,
  sendRefreshOffersEvent: false,
  alternativeConfirmStatusUrl: undefined,
  orderSummary: {
    orderSummaryData: null,
    orderStatus: OrderSummaryStatus.INITIAL,
  },
  reviewConfirmed: false,
  acknowledgeFlow: false,
  showAlternativeOffers: false,
});

const reducer = createReducer(
  initialState,
  on(AlternativeOffersActions.reset, (state) => {
    // sendRefreshOffersEvent needs to persist for tracking purposes trough deeplink and not be reseted
    return {
      ...initialState,
      sendRefreshOffersEvent: state.sendRefreshOffersEvent,
    };
  }),
  on(AlternativeOffersActions.setStatus, (state, { status }) => {
    return {
      ...state,
      status,
    };
  }),
  on(AlternativeOffersActions.setShowAlternativeOffers, (state, { show }) => {
    return {
      ...state,
      showAlternativeOffers: show,
    };
  }),
  on(AlternativeOffersActions.setOffers, (state, { boundIndex, alternativeOffers }) => {
    return {
      ...state,
      alternativeOffers: {
        ...state.alternativeOffers,
        [boundIndex]: alternativeOffers,
      },
    };
  }),
  on(AlternativeOffersActions.resetOffers, (state) => {
    return {
      ...state,
      alternativeOffers: undefined,
    };
  }),
  on(AlternativeOffersActions.setDisruptedBoundIndex, (state, { disruptedBoundIndex }) => {
    return {
      ...state,
      disruptedBoundIndex,
    };
  }),
  on(AlternativeOffersActions.setSelectedOffer, (state, { boundIndex, id }) => {
    return {
      ...state,
      selectedOffers: {
        ...state.selectedOffers,
        [boundIndex]: id,
      },
    };
  }),
  on(AlternativeOffersActions.resetSelectedOffers, (state) => {
    return {
      ...state,
      selectedOffers: {},
    };
  }),
  on(AlternativeOffersActions.setSelectedDate, (state, { boundIndex, date }) => {
    return {
      ...state,
      selectedDates: {
        ...state.selectedDates,
        [boundIndex]: date,
      },
    };
  }),
  on(AlternativeOffersActions.setStage, (state, { stage }) => {
    return {
      ...state,
      currentStage: stage,
    };
  }),
  on(AlternativeOffersActions.setDeeplinkSource, (state, { source }) => {
    return {
      ...state,
      deeplinkSource: source,
    };
  }),
  on(AlternativeOffersActions.resetDeeplinkSource, (state) => {
    return {
      ...state,
      deeplinkSource: undefined,
    };
  }),
  on(AlternativeOffersActions.setConfirmationResponse, (state, { confirmationResponse }) => {
    return {
      ...state,
      confirmationResponse,
    };
  }),
  on(AlternativeOffersActions.setSessionExpired, (state) => {
    return {
      ...state,
      sessionExpired: true,
    };
  }),
  on(AlternativeOffersActions.setSessionStarted, (state, { sessionStartedMs }) => {
    return {
      ...state,
      sessionStartedMs,
    };
  }),
  on(AlternativeOffersActions.setBooking, (state, { booking }) => {
    return {
      ...state,
      booking,
    };
  }),
  on(AlternativeOffersActions.setSendRefreshOffersEvent, (state, { sendRefreshOffersEvent }) => {
    return {
      ...state,
      sendRefreshOffersEvent,
    };
  }),
  on(AlternativeOffersActions.setAlternativeConfirmUrl, (state, { url }) => {
    return {
      ...state,
      alternativeConfirmStatusUrl: url,
    };
  }),
  on(AlternativeOffersActions.setAcknowledgeFlow, (state, { acknowledgeFlow }) => {
    return {
      ...state,
      acknowledgeFlow,
    };
  }),
  on(AlternativeOffersActions.setOrderSummaryData, (state, { data }) => {
    return {
      ...state,
      orderSummary: {
        ...state.orderSummary,
        orderStatus: OrderSummaryStatus.OK,
        orderSummaryData: data,
      },
    };
  }),
  on(AlternativeOffersActions.setOrderReset, (state) => {
    return {
      ...state,
      orderSummary: {
        ...initialState.orderSummary,
      },
    };
  }),
  on(AlternativeOffersActions.setOrderStart, (state) => {
    return {
      ...state,
      orderSummary: {
        ...state.orderSummary,
        orderStatus: OrderSummaryStatus.PENDING,
      },
    };
  }),
  on(AlternativeOffersActions.setOrderError, (state) => {
    return {
      ...state,
      orderSummary: {
        ...state.orderSummary,
        orderStatus: OrderSummaryStatus.LOAD_ERROR,
      },
    };
  }),
  on(AlternativeOffersActions.setReviewConfirmed, (state, { reviewConfirmed }) => {
    return {
      ...state,
      reviewConfirmed,
    };
  })
);

export function alternativeOffersReducer(
  state = initialState,
  action: AlternativeOffersActions.ActionsUnion
): AlternativeOffersState {
  return reducer(state, action);
}
